/**
 * connectCloudProjectRuntime — provision-if-needed + connect for a cloud
 * project, shared by the sidebar row's connect-on-action path and by new-thread
 * creation.
 *
 * POST the Forge-server proxy (authenticated by session cookie, forwarded to
 * CMMD): `/ensure` first (idempotent — it CREATES the sprite the first time and
 * is a cheap no-op afterwards), then `/connect`, retrying on 5xx while the VM
 * boots, then register the returned descriptor as a connected saved
 * environment. Throws on failure; the caller owns busy state + error display
 * (translate via formatCloudConnectStartError). The actual CMMD calls live in
 * `connectCloudRuntimeProvisioning.ts` (`runCloudRuntimeConnection`) — this
 * file owns the public entry point plus the concurrent-caller dedup below it.
 *
 * This is the ONLY place a sprite is created. Import does not provision — it
 * registers the project and returns immediately — so the first thread on a
 * project pays the cold-provision cost, and every later thread reuses the
 * runtime that `/ensure` reports back.
 *
 * `meta.runtimeId` is null on that first call: the id is read out of the ensure
 * response rather than guessed, so no code path ever holds an id for a runtime
 * that does not exist.
 */
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary/target";
import { buildPrimaryFetchInit } from "~/environments/primary/fetchInit";
import { listSavedEnvironmentRecords } from "~/environments/runtime/catalog";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import type { SidebarPreConnectCloudMetadata } from "./SidebarProjectItem";
import { runCloudRuntimeConnection } from "./connectCloudRuntimeProvisioning";
import { assertForgeCloudServerPresentForConnect } from "../../lib/missingForgeCloudServer";
import { createAbortError } from "./connectCloudRuntimeShared";

const STATUS_REFRESH_TIMEOUT_MS = 2_500;
const CONNECT_LIFECYCLE_TIMEOUT_MS = 5 * 60_000;

export type CloudRuntimeConnectStage =
  | "desktop-bridge"
  | "runtime-ensure-request"
  | "runtime-ensure-retry"
  | "runtime-ensured"
  | "runtime-ensure-failed"
  | "runtime-start-request"
  | "runtime-started"
  | "runtime-start-failed"
  | "connect-request"
  | "connect-retry"
  | "connection-descriptor"
  | "environment-register"
  | "environment-register-retry"
  | "clone-request"
  | "clone-registered"
  | "connected";

export interface CloudRuntimeConnectProgressEvent {
  readonly stage: CloudRuntimeConnectStage;
  readonly summary: string;
  readonly detail?: string;
  readonly attempt?: number;
  readonly maxAttempts?: number;
  readonly at: string;
}

export type CloudRuntimeConnectProgressReporter = (event: CloudRuntimeConnectProgressEvent) => void;

export interface ConnectedCloudProjectEnvironment {
  readonly runtimeId: string;
  readonly environmentId: EnvironmentId;
}

interface CloudRuntimeConnectionFlight {
  promise: Promise<string>;
  readonly controller: AbortController;
  readonly progressReporters: Set<CloudRuntimeConnectProgressReporter>;
  latestProgress: CloudRuntimeConnectProgressEvent | null;
  waiterCount: number;
  settled: boolean;
}

const cloudRuntimeConnectionFlights = new Map<string, CloudRuntimeConnectionFlight>();

/**
 * In-flight dedup key. Keyed at the SAME grain as the runtime uniqueness index
 * — NOT on the runtime id.
 *
 * Keying on the runtime id would not dedup the case this exists for: two
 * threads started at once on a project with no runtime both carry
 * `runtimeId: null`, so they would race two provisions of the same sprite.
 * Project identity is stable from before the first provision through every
 * later recycle.
 *
 * FOR-2405 split this by task and FOR-32652 un-split it, because FOR-32649
 * retired the task dimension from the index it mirrors. That split was not
 * merely stale: with one project runtime again, two threads launched from two
 * tasks in ONE project took two different flights and raced two provisions of
 * that single runtime — the very double-provision this key exists to prevent,
 * pointed the other way. It was unreachable only because nothing ever populated
 * the field.
 *
 * It is deliberately NOT re-split by the Environment an ensure may now name
 * (FOR-32652). CMMD's own `runtimeIdentityLockKey` is still derived from
 * (org, user, project) for exactly this reason: if naming an Environment
 * changed the key, a client that sends one and a client that does not would
 * miss each other's flight and each provision. The identifier selects a row; it
 * is not a slot.
 */
function cloudProjectFlightKey(meta: SidebarPreConnectCloudMetadata): string {
  return `${meta.cmmdOrganizationId}:${meta.cmmdProjectId}`;
}

export const __cloudProjectFlightKeyForTests = cloudProjectFlightKey;

async function fetchRuntimeStatusWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), STATUS_REFRESH_TIMEOUT_MS);
  try {
    return await fetch(url, buildPrimaryFetchInit({ method: "GET", signal: controller.signal }));
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * Best-effort: ask the server to reconcile ONE cloud runtime against live
 * substrate state (read-only — never wakes the machine) so a runtime that is
 * stopped, suspended, or gone entirely reads as such instead of a stale
 * `running`. The server persists the reconciled status, so the existing
 * runtime-list SSE then delivers it to the sidebar + the auto-connect gate.
 * Fire-and-forget; failures are swallowed (the list still converges via the
 * background reconciler).
 */
export async function refreshCloudRuntimeStatus(runtimeId: string): Promise<void> {
  try {
    await fetchRuntimeStatusWithTimeout(
      resolvePrimaryEnvironmentHttpUrl(
        `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/status`,
      ),
    );
  } catch {
    // Best-effort truth refresh — ignore network errors.
  }
}

function startCloudRuntimeConnectionFlight(
  meta: SidebarPreConnectCloudMetadata,
): CloudRuntimeConnectionFlight {
  const controller = new AbortController();
  const flightKey = cloudProjectFlightKey(meta);
  const flight: CloudRuntimeConnectionFlight = {
    promise: Promise.resolve(""),
    controller,
    progressReporters: new Set(),
    latestProgress: null,
    waiterCount: 0,
    settled: false,
  };
  cloudRuntimeConnectionFlights.set(flightKey, flight);

  let didTimeout = false;
  const timeoutId = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, CONNECT_LIFECYCLE_TIMEOUT_MS);
  const operation = runCloudRuntimeConnection(meta, {
    signal: controller.signal,
    onProgress: (event) => {
      flight.latestProgress = event;
      for (const reporter of flight.progressReporters) reporter(event);
    },
  }).catch((error: unknown) => {
    if (didTimeout) {
      throw new Error(
        "Cloud project did not become ready within 5 minutes. Retry to start a fresh connection.",
        { cause: error },
      );
    }
    throw error;
  });
  flight.promise = operation.finally(() => {
    window.clearTimeout(timeoutId);
    flight.settled = true;
    if (cloudRuntimeConnectionFlights.get(flightKey) === flight) {
      cloudRuntimeConnectionFlights.delete(flightKey);
    }
  });
  return flight;
}

function waitForCloudRuntimeConnectionFlight(
  flight: CloudRuntimeConnectionFlight,
  options?: {
    readonly signal?: AbortSignal;
    readonly onProgress?: CloudRuntimeConnectProgressReporter;
  },
): Promise<string> {
  const reporter = options?.onProgress;
  if (reporter) {
    flight.progressReporters.add(reporter);
    if (flight.latestProgress) reporter(flight.latestProgress);
  }
  flight.waiterCount += 1;

  return new Promise<string>((resolve, reject) => {
    let released = false;
    const release = (didAbort: boolean) => {
      if (released) return;
      released = true;
      options?.signal?.removeEventListener("abort", onAbort);
      if (reporter) flight.progressReporters.delete(reporter);
      flight.waiterCount -= 1;
      if (didAbort && flight.waiterCount === 0 && !flight.settled) {
        flight.controller.abort();
      }
    };
    const onAbort = () => {
      release(true);
      reject(createAbortError());
    };

    if (options?.signal?.aborted) {
      onAbort();
      return;
    }
    options?.signal?.addEventListener("abort", onAbort, { once: true });
    void flight.promise.then(
      (runtimeId) => {
        release(false);
        resolve(runtimeId);
      },
      (error: unknown) => {
        release(false);
        reject(error);
      },
    );
  });
}

/**
 * Provision (if needed) and connect the cloud runtime for a project. Returns the
 * runtime id CMMD settled on, so callers can address the environment without
 * waiting for the catalog to catch up.
 *
 * Concurrent callers for the same project share ONE flight, so N threads
 * started at once still produce exactly one sprite.
 */
export async function connectCloudProjectRuntime(
  meta: SidebarPreConnectCloudMetadata,
  options?: {
    readonly signal?: AbortSignal;
    readonly onProgress?: CloudRuntimeConnectProgressReporter;
  },
): Promise<string> {
  assertForgeCloudServerPresentForConnect();
  const flight =
    cloudRuntimeConnectionFlights.get(cloudProjectFlightKey(meta)) ??
    startCloudRuntimeConnectionFlight(meta);
  return waitForCloudRuntimeConnectionFlight(flight, options);
}

/**
 * Connect a cloud project and return both identities the caller needs.
 *
 * The Runtime ID addresses the current Sprite. The Environment ID addresses
 * the saved command connection that `runCloudRuntimeConnection` registered.
 * Reading that record after the awaited connect also covers first provision,
 * when the caller's catalog row predates CMMD's newly assigned Environment ID.
 */
export async function connectCloudProjectEnvironment(
  meta: SidebarPreConnectCloudMetadata,
  options?: {
    readonly signal?: AbortSignal;
    readonly onProgress?: CloudRuntimeConnectProgressReporter;
  },
): Promise<ConnectedCloudProjectEnvironment> {
  const runtimeId = await connectCloudProjectRuntime(meta, options);
  const matchingRecords = listSavedEnvironmentRecords().filter(
    (record) => record.runtimeId === runtimeId,
  );
  const [record] = matchingRecords;
  if (matchingRecords.length !== 1 || record === undefined) {
    throw new Error("Cloud runtime connected, but its Environment registration was not ready.");
  }
  return {
    runtimeId,
    environmentId: record.environmentId,
  };
}
